'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  HUBSPOT_ERROR_CODES,
  GENERIC_HUBSPOT_ERROR_MESSAGE,
  type HubSpotErrorCode,
} from '@workspace/constants/contact-form.constant';
import { contactFormSchema } from '@workspace/schema/contact-form.schema';
import { createNewsletterSubmissionData } from '@workspace/lib/hubspot-utils';
import { NEWSLETTER_MESSAGES } from '@workspace/constants/newsletter.constant';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { NewsletterSubmitArgs } from './newsletter.types';

const schema = z.object({
  email: contactFormSchema.shape.email,
  honeypot: z.string().optional(),
});

export function useNewsletter() {
  const [showSuccess, setShowSuccess] = useState(false);
  const GENERIC_ERROR = GENERIC_HUBSPOT_ERROR_MESSAGE;
  const [loading, setLoading] = useState(false);
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const submit = async (args: NewsletterSubmitArgs): Promise<any> => {
    try {
      const response = await fetch('/api/hubspot/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...args, submittedAt: Date.now() }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        const code = (data?.code as HubSpotErrorCode) ?? HUBSPOT_ERROR_CODES.UNKNOWN_ERROR;
        throw new Error(code);
      }

      return {
        success: true,
        message: data?.message ?? 'Newsletter subscribed successfully',
      };
    } catch {
      return { success: false, error: GENERIC_ERROR };
    }
  };

  const onSubmit = async (values: z.infer<typeof schema>) => {
    if (loading) return;
    setLoading(true);
    setShowSuccess(false);
    try {
      if (values.honeypot) {
        return;
      }
      const payload = createNewsletterSubmissionData([{ name: 'email', value: values.email }], false);
      const res = await submit(payload);

      if (res.success) {
        form.reset();
        setShowSuccess(true);
      } else {
        form.setError('email', {
          message: res.error || NEWSLETTER_MESSAGES.ERROR,
        });
      }
    } finally {
      // ensure loading false always at end
      setLoading(false);
    }
  };

  return {
    form,
    onSubmit,
    showSuccess,
    loading,
  };
}
